import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import {
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Key as KeyIcon } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import { getAppSettings, updateAssemblyAIApiKey, updateOpenAIApiKeys } from '../../../firebase/services/appSettingsService';
import ApiKeySettings from './ApiKeySettings';

const ApiSettings = () => {
  const { addSystemNotification } = useNotifications();
  const [settings, setSettings] = useState({
    assemblyAI: {
      apiKey: '',
    },
    openAI: {
      apiKey: '',
      organizationId: '',
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Configuration du style moderne sobre
  const glassBoxSx = {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)',
    }
  };

  // Charger les paramètres
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const appSettings = await getAppSettings();
        
        setSettings({
          assemblyAI: {
            apiKey: appSettings?.assemblyAISettings?.apiKey || '',
          },
          openAI: {
            apiKey: appSettings?.openAISettings?.apiKey || '',
            organizationId: appSettings?.openAISettings?.organizationId || '',
          }
        });
      } catch (error) {
        addSystemNotification('Erreur lors du chargement des paramètres:', 'error');
        setSaveStatus({
          type: 'error',
          message: 'Erreur lors du chargement des paramètres: ' + error.message
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSaveAssemblyAIKey = async (apiKeyData) => {
    try {
      setSaving(true);
      setSaveStatus(null);
      
      await updateAssemblyAIApiKey(apiKeyData.apiKey);
      
      setSettings(prev => ({
        ...prev,
        assemblyAI: {
          ...prev.assemblyAI,
          apiKey: apiKeyData.apiKey
        }
      }));
      
      setSaveStatus({
        type: 'success',
        message: 'Clé API AssemblyAI enregistrée avec succès!'
      });
    } catch (error) {
      addSystemNotification('Erreur lors de la sauvegarde de la clé API AssemblyAI:', 'error');
      setSaveStatus({
        type: 'error',
        message: 'Erreur lors de la sauvegarde: ' + error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOpenAIKeys = async (apiKeyData) => {
    try {
      setSaving(true);
      setSaveStatus(null);
      
      await updateOpenAIApiKeys({
        apiKey: apiKeyData.apiKey,
        organizationId: apiKeyData.organizationId
      });
      
      setSettings(prev => ({
        ...prev,
        openAI: {
          ...prev.openAI,
          apiKey: apiKeyData.apiKey,
          organizationId: apiKeyData.organizationId || ''
        }
      }));
      
      setSaveStatus({
        type: 'success',
        message: 'Clés API OpenAI enregistrées avec succès!'
      });
    } catch (error) {
      addSystemNotification('Erreur lors de la sauvegarde des clés API OpenAI:', 'error');
      setSaveStatus({
        type: 'error',
        message: 'Erreur lors de la sauvegarde: ' + error.message
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 400,
        ...glassBoxSx,
        m: 3
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2, color: '#3F51B5' }} />
          <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
            Chargement des clés API...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Box sx={{ 
        ...glassBoxSx,
        mb: 3,
        p: 3,
        backgroundColor: '#f8fafc',
        border: '1px solid rgba(63, 81, 181, 0.1)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
            boxShadow: '0 6px 20px rgba(63, 81, 181, 0.3)',
          }}>
            <KeyIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                color: '#1f2937',
                mb: 0.5
              }}
            >
              🔑 Gestion des Clés API
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Configurez les clés API pour les services de transcription et d'IA
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Messages de statut */}
      {saveStatus && (
        <Alert 
          severity={saveStatus.type} 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            backgroundColor: 'white',
            border: saveStatus.type === 'success' 
              ? '1px solid rgba(34, 197, 94, 0.2)' 
              : '1px solid rgba(239, 68, 68, 0.2)',
            boxShadow: saveStatus.type === 'success' 
              ? '0 4px 16px rgba(34, 197, 94, 0.08)' 
              : '0 4px 16px rgba(239, 68, 68, 0.08)'
          }}
          onClose={() => setSaveStatus(null)}
        >
          {saveStatus.type === 'success' ? '✅' : '❌'} {saveStatus.message}
        </Alert>
      )}
      
      {/* Section AssemblyAI */}
      <Box sx={{ ...glassBoxSx, mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 700, mb: 2 }}>
          🎤 AssemblyAI
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
          Service de transcription automatique de l'audio en texte
        </Typography>
        <ApiKeySettings
          serviceName="AssemblyAI"
          apiKey={settings.assemblyAI.apiKey}
          onSave={handleSaveAssemblyAIKey}
          saving={saving}
          hasOrganizationId={false}
        />
      </Box>
      
      {/* Section OpenAI */}
      <Box sx={{ ...glassBoxSx, p: 3 }}>
        <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 700, mb: 2 }}>
          🤖 OpenAI
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
          Service d'intelligence artificielle pour la génération de résumés
        </Typography>
        <ApiKeySettings
          serviceName="OpenAI"
          apiKey={settings.openAI.apiKey}
          organizationId={settings.openAI.organizationId}
          onSave={handleSaveOpenAIKeys}
          saving={saving}
          hasOrganizationId={true}
        />
      </Box>
    </Box>
  );
};

export default ApiSettings; 